import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Phone, Send, Loader2, CheckCircle2 } from 'lucide-react';
import { ValidatedField, ValidationSummary, useFieldValidation, invalidProps } from '@/components/forms/ValidatedField';
import { zodFieldErrors } from '@/lib/forms/formValidation';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { z } from 'zod';
import { useLanguage } from '@/contexts/LanguageContext';

interface SalesContactFormProps {
  trigger?: React.ReactNode;
  buttonVariant?: 'default' | 'outline' | 'secondary' | 'ghost';
  buttonText?: string;
  buttonClassName?: string;
}

import React from 'react';

export const SalesContactForm = React.forwardRef<HTMLDivElement, SalesContactFormProps>(function SalesContactForm({ 
  trigger, 
  buttonVariant = 'outline',
  buttonText,
  buttonClassName = ''
}, ref) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    message: '',
  });

  const contactSchema = z.object({
    name: z.string().trim().min(2, t.salesNameTooShort).max(100, t.salesNameTooLong),
    email: z.string().trim().email(t.salesEmailInvalid).max(255, t.salesEmailTooLong),
    phone: z.string().trim().optional(),
    organization: z.string().trim().optional(),
    message: z.string().trim().min(10, t.salesMsgTooShort).max(2000, t.salesMsgTooLong),
  });

  const SALES_FIELDS = [
    { key: 'name', id: 'name', label: t.salesContactName },
    { key: 'email', id: 'email', label: t.salesContactEmail },
    { key: 'message', id: 'message', label: t.salesContactMessage },
  ];
  const { errors: fieldErrors, validate, reset: resetValidation } = useFieldValidation(
    () => zodFieldErrors(contactSchema.safeParse(formData).error),
    SALES_FIELDS,
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke('send-email', {
        body: {
          to: 'Giorgio.arghittu@refertius.com',
          subject: `[Refertius Vendite] Richiesta da ${formData.name}`,
          type: 'sales_contact',
          data: {
            name: formData.name,
            email: formData.email,
            phone: formData.phone || undefined,
            organization: formData.organization || undefined,
            message: formData.message,
          }
        }
      });

      if (error) throw error;

      await supabase.functions.invoke('send-email', {
        body: {
          to: formData.email,
          subject: 'Conferma richiesta contatto - Refertius',
          type: 'sales_contact_confirmation',
          data: {
            name: formData.name,
            email: formData.email,
            phone: formData.phone || undefined,
            organization: formData.organization || undefined,
            message: formData.message,
          }
        }
      });

      setSuccess(true);
      toast.success(t.salesContactSuccess);
    } catch (err) {
      console.error('Error sending contact form:', err);
      toast.error(t.salesContactError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant={buttonVariant} className={buttonClassName}>
            <Phone className="mr-2 h-4 w-4" />
            {buttonText || t.pricingContactSales}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-primary" />
            {t.salesContactTitle}
          </DialogTitle>
          <DialogDescription>
            {t.salesContactDesc}
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-8 text-center">
            <CheckCircle2 className="h-12 w-12 mx-auto mb-4" style={{ color: 'hsl(var(--success))' }} />
            <p className="text-lg font-medium">{t.salesContactSent}</p>
            <p className="text-muted-foreground text-sm mt-1 mb-4">
              {t.salesContactSentDesc}
            </p>
            <Button 
              variant="outline" 
              onClick={() => {
                setOpen(false);
                setSuccess(false);
                resetValidation();
                setFormData({ name: '', email: '', phone: '', organization: '', message: '' });
              }}
            >
              {t.salesContactClose}
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate className="space-y-4 mt-4">
            <ValidationSummary errors={fieldErrors} fields={SALES_FIELDS} testId="sales-validation-summary" />
            <div className="grid grid-cols-2 gap-4">
              <ValidatedField id="name" label={t.salesContactName} required error={fieldErrors.name}>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t.salesContactPlaceholderName}
                />
              </ValidatedField>
              <ValidatedField id="email" label={t.salesContactEmail} required error={fieldErrors.email}>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t.salesContactPlaceholderEmail}
                />
              </ValidatedField>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">{t.salesContactPhone}</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder={t.salesContactPlaceholderPhone}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="organization">{t.salesContactOrg}</Label>
                <Input
                  id="organization"
                  name="organization"
                  value={formData.organization}
                  onChange={handleChange}
                  placeholder={t.salesContactPlaceholderOrg}
                />
              </div>
            </div>

            <ValidatedField id="message" label={t.salesContactMessage} required error={fieldErrors.message}>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder={t.salesContactPlaceholderMsg}
                rows={4}
              />
            </ValidatedField>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t.salesContactSending}
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  {t.salesContactSend}
                </>
              )}
            </Button>

            <p className="text-xs text-muted-foreground text-center">
              {t.salesContactPrivacy}{' '}
              <a href="/privacy" className="underline hover:text-foreground">{t.salesContactPrivacyLink}</a>.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
});
